import { Component, Input, OnInit } from '@angular/core';

import {
  ConnectionState,
  ConnectionStatus,
  Provider,
} from '../../../api-clients/oem-connection';
import { OemIntegrationService } from '../../../services/oem-integration.service';
import { PROVIDER_LABELS } from '../../integrations/integrations.component';

interface ProviderLinkRow {
  provider: Provider;
  label: string;
  status: ConnectionStatus;
  /** True when the vendor OAuth connection is live (`active`). */
  active: boolean;
}

/**
 * OEM machine → harvester linkage section for the harvester create/edit page
 * (SRA003-457). Reuses the connection page's machine-link-panel (same
 * endpoints, same store — `harvesters.oem_provider`/`oem_external_id`), so a
 * linkage created here is reflected on the OEM connection page and vice
 * versa. Per provider: the panel is shown only when the vendor connection is
 * active; otherwise a "connect the vendor first" hint pointing at the
 * Integrations page. Provider-agnostic (Deere + CNH).
 */
@Component({
  selector: 'app-harvester-oem-link-section',
  templateUrl: './harvester-oem-link-section.component.html',
  styleUrls: ['./harvester-oem-link-section.component.css'],
  standalone: false,
})
export class HarvesterOemLinkSectionComponent implements OnInit {
  /** Harvester being edited — preselects the panel's link pickers. */
  @Input() preselectHarvesterId?: number;

  /** False until connections load; the section stays hidden meanwhile. */
  loaded = false;
  rows: ProviderLinkRow[] = [];

  // Expose Provider for the template's grants gating (Deere only).
  readonly Provider = Provider;

  constructor(private oemSvc: OemIntegrationService) {}

  ngOnInit(): void {
    this.oemSvc.listConnections().subscribe({
      next: (list) => {
        this.rows = [
          this.toRow(Provider.Deere, list?.deere),
          this.toRow(Provider.Cnh, list?.cnh),
        ];
        this.loaded = true;
      },
      error: () => {
        // Connection state unknown — keep the whole section hidden rather
        // than degrade the harvester form (the linkage stays available on
        // the Integrations page).
        this.rows = [];
        this.loaded = false;
      },
    });
  }

  private toRow(
    provider: Provider,
    status: ConnectionStatus | undefined,
  ): ProviderLinkRow {
    const safeStatus: ConnectionStatus = status ?? {
      status: ConnectionState.NotConnected,
    };
    return {
      provider,
      label: PROVIDER_LABELS[provider],
      status: safeStatus,
      active: safeStatus.status === ConnectionState.Active,
    };
  }
}
